import { Tooltip } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';

import { usePanel } from '~/src/dash/components/layout/contexts';
import {
  Col,
  Cols,
  fontSizing,
  Row,
  Rows,
  spacing,
  Text,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { getOversizedLanguageUIFriendlyStyle } from '~/src/dash/publicaUI/common';
import { useBaseState, useBaseStore } from '~/src/dash/views/base/contexts';
import { INavBarSubItem, IPermissionParams } from '~/src/dash/views/navbar/configs/defaultCfg';
import { useNavBarStore } from '~/src/dash/views/navbar/contexts';
import { getMenuBackgroundColor } from '~/src/dash/views/navbar/NavBar/common/consts';
import { NavBarSubRow } from '~/src/dash/views/navbar/NavBar/ExpandableNavBarRow/NavBarSubRow';

const settingsViews = [
  'publisher-settings',
  'account-settings',
  'billing-details',
  'audience-data-share-manager',
  'change-logs',
  'ip-trace',
  'team',
];

interface IExpandableNavBarRowProps {
  label: string;
  iconPath: string;
  // pass in any path names you want to check to ensure this mini dropdown doesn't collapse
  tooltipText: React.ReactNode;
  isDisabled: boolean;
  subViews: string[];
  subItems: INavBarSubItem[];
  iconPathDark: string;
  handleNavExpand: () => void;
  permissionParams: IPermissionParams;
  tKey?: TKey;
  uiFriendlyTKey?: TKey; // used if a string needs to be modified to better fit the ui
}

export const ExpandableNavBarRow = ({
  label,
  iconPath,
  tooltipText,
  isDisabled,
  subViews,
  subItems,
  iconPathDark,
  handleNavExpand,
  permissionParams,
  tKey,
  uiFriendlyTKey,
}: IExpandableNavBarRowProps) => {
  const { location } = useBaseStore();
  const { isNavExpanded } = useBaseState();
  const { activeSubMenu, setActiveSubMenu, setResizeAction, isDarkTheme } = useNavBarStore();
  const { theme } = usePanel();
  const { i18n } = usePublicaTranslation();
  const itemsRef = useRef<HTMLDivElement>(null);

  const currentPath = location.pathname;
  const isSettingsView = settingsViews.some((view) => currentPath.indexOf(view) > -1);
  const hasViewInPath = isSettingsView
    ? false
    : subViews.some((view) => currentPath.indexOf(view) > -1);
  const isActive = hasViewInPath;

  const [isHover, setIsHover] = useState(false);
  const [isOpen, setIsOpen] = useState(hasViewInPath);
  const [height, setHeight] = useState(0);

  // check to see if submenu should be open
  useEffect(() => {
    setIsOpen(hasViewInPath);
  }, [location.pathname]);

  // close submenu if this is inactive and another submenu opens
  useEffect(() => {
    if (isOpen && !hasViewInPath && label !== activeSubMenu) {
      setIsOpen(false);
    }
  }, [activeSubMenu]);

  // set active submenu and handle animation via height
  useEffect(() => {
    if (isOpen && !isActive) {
      setActiveSubMenu(label);
    }
    const itemsHeight = itemsRef.current?.scrollHeight || 0;
    setHeight(isOpen ? itemsHeight : 0);
    setTimeout(() => {
      setResizeAction({ action: 'resize' });
    }, 400);
  }, [isOpen]);

  const isSelectedDark = (isActive || isOpen || isHover) && isDarkTheme;

  return (
    <>
      <Tooltip
        title={isNavExpanded ? '' : tooltipText || label}
        placement='right'
        disableHoverListener={isNavExpanded}
      >
        <button
          style={{
            padding: 0,
            margin: 0,
            width: '100%',
            border: 'none',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            borderBottomLeftRadius: isOpen && isNavExpanded ? '0px' : '4px',
            borderBottomRightRadius: isOpen && isNavExpanded ? '0px' : '4px',
            backgroundColor:
              (isOpen && isNavExpanded) || (isActive && !isNavExpanded)
                ? isHover
                  ? theme.menuBarHovered
                  : theme.menuBarSelected
                : getMenuBackgroundColor(isHover, isActive, theme),
            borderBottom: `${isOpen ? '1px solid' + theme.menuBarBackground : 'none'}`,
            marginBottom: isOpen ? '0px' : '1px',
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
          }}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          onClick={() => {
            if (!isNavExpanded) {
              handleNavExpand();
              setIsOpen(true);
            } else {
              setIsOpen(!isOpen);
            }
          }}
          disabled={isDisabled}
        >
          <Cols
            style={{
              height: '32px',
              alignItems: 'center',
              cursor: isDisabled ? 'default' : 'pointer',
            }}
          >
            <Col smallPadding>
              <img
                src={isSelectedDark ? iconPathDark : iconPath}
                width='16px'
                height='16px'
                style={{ opacity: isDisabled ? 0.7 : 1 }}
              />
            </Col>
            {isNavExpanded && (
              <Col flex smallPadding>
                <Cols
                  style={{
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Col>
                    <Text
                      color={isSelectedDark ? '#000' : theme.menuBarText}
                      style={{
                        whiteSpace: 'nowrap',
                        opacity: isDisabled ? 0.7 : 1,
                        ...getOversizedLanguageUIFriendlyStyle(i18n.language),
                      }}
                      tKey={uiFriendlyTKey || tKey}
                    >
                      {label}
                    </Text>
                  </Col>
                  <Col paddingTop={spacing.extraSmall}>
                    <Text color={theme.menuBarText} style={{ opacity: isDisabled ? 0.7 : 1 }}>
                      {isOpen ? (
                        <ExpandLess
                          style={{
                            fontSize: fontSizing.small,
                            color: isSelectedDark ? '#000' : theme.menuBarText,
                          }}
                        />
                      ) : (
                        <ExpandMore
                          style={{
                            fontSize: fontSizing.small,
                            color: isSelectedDark ? '#000' : theme.menuBarText,
                          }}
                        />
                      )}
                    </Text>
                  </Col>
                </Cols>
              </Col>
            )}
          </Cols>
        </button>
      </Tooltip>
      <div
        ref={itemsRef}
        style={{
          height: `${height}px`,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          transition: 'height 0.3s',
        }}
      >
        <Rows>
          {subItems.map((item) => {
            if (item.checkIfHidden && item.checkIfHidden(permissionParams)) return false;
            return (
              <Row noPadding>
                <NavBarSubRow
                  label={item.label}
                  route={item.route}
                  isDisabled={isDisabled}
                  permChecks={item.permChecks}
                  pathString={item.pathString}
                  customActiveCheck={item.customActiveCheck}
                  eventName={item.eventName}
                  isBeta={item.isBeta}
                  tKey={item.tKey}
                  uiFriendlyTKey={item.uiFriendlyTKey}
                />
              </Row>
            );
          })}
        </Rows>
      </div>
    </>
  );
};
